import React, { Fragment } from "react";
import { List, ListItem, AccordionContent } from "framework7-react";
import _ from "lodash";
import { createMenuItems, createCustomFormLinks } from "../../web/AppSidebar/helpers";
import { logout } from "modules/auth/api";
import { clearAllLocalData } from "modules/offline/clearLocalData";

export const MOBILE_MODULE_SIDEBARS = {
  quality_assurance: "qa",
  store: "store",
};

export const ROOT_MENU_LOCATIONS = [
  "main_menu",
  "production",
  "administration",
  "drawing_office",
  "training",
  "hireages",
  "settings",
];

export const getMobileCustomForms = (custom_forms) =>
  _.filter(custom_forms, (form) => (form.include_parent_index_page || !form.parent_type) && !form.archived && form.on_mobile);

export const getMobileMenus = (custom_forms_menus) => _.filter(custom_forms_menus, (menu) => menu.on_mobile);

export const createMobileCustomFormLinks = (forms) => createCustomFormLinks(getMobileCustomForms(forms));

export const createMobileMenuItems = (menuItems, custom_forms) => {
  const mobileFormIds = new Set(_.map(getMobileCustomForms(custom_forms), "id"));
  const mobileMenus = _(menuItems)
    .filter((menu) => menu.on_mobile)
    .map((menu) => ({
      ...menu,
      custom_forms: _.filter(menu.custom_forms, (custom_form) => mobileFormIds.has(custom_form.id)),
    }))
    .value();

  return createMenuItems(mobileMenus);
};

export const buildLocationNavItems = (location, mobileCustomForms, mobileMenus, custom_forms) => {
  const forms = _.filter(mobileCustomForms, (form) => form.menu_location === location);
  const menus = _.filter(
    mobileMenus,
    (menu) => menu.parent_type === location && (location !== "settings" || !menu.parent_id)
  );

  return _.concat(createMobileCustomFormLinks(forms), createMobileMenuItems(menus, custom_forms));
};

export const buildRootNavItems = (mobileCustomForms, mobileMenus, custom_forms) =>
  _.flatMap(ROOT_MENU_LOCATIONS, (location) => buildLocationNavItems(location, mobileCustomForms, mobileMenus, custom_forms));

export const getMobileSidebarType = (pathname, custom_form, custom_forms_menus) => {
  if (_.includes(pathname, "qa")) return "qa";
  if (_.includes(pathname, "store")) return "store";
  if (/^\/custom_forms\/menus\/\d+\/module/.test(pathname)) return "custom_forms_menu_module";

  if (custom_form) {
    if (MOBILE_MODULE_SIDEBARS[custom_form.menu_location]) {
      return MOBILE_MODULE_SIDEBARS[custom_form.menu_location];
    }

    if (custom_form.menu_location !== "custom_menu" || !custom_form.menu_id) {
      return "main";
    }

    let menu = custom_forms_menus[custom_form.menu_id];
    while (menu?.parent_type === "custom_menu" && menu.parent_id) {
      menu = custom_forms_menus[menu.parent_id];
    }
    if (!menu) return "main";
    if (menu.menu_type === "module") return "custom_forms_menu_module";
    return MOBILE_MODULE_SIDEBARS[menu.parent_type] || "main";
  }

  return "main";
};

export const getMenuModuleId = (pathname, custom_form, custom_forms_menus) => {
  const moduleMatch = pathname.match(/\/custom_forms\/menus\/(\d+)\/module/);
  if (moduleMatch) return moduleMatch[1];

  if (!custom_form?.menu_id) return null;

  let menu = custom_forms_menus[custom_form.menu_id];
  while (menu && menu.menu_type !== "module" && menu.parent_type === "custom_menu" && menu.parent_id) {
    menu = custom_forms_menus[menu.parent_id];
  }

  return menu?.menu_type === "module" ? menu.id : null;
};

const parentBackLinks = {
  production: { label: "Back To Lion", link: "/" },
  quality_assurance: { label: "Back To QA", link: "/project/qa" },
  store: { label: "Back To Store", link: "/store" },
};

export const getMobileCustomFormMenuModuleNav = (custom_forms_menus, menuId, custom_forms) => {
  const menu = custom_forms_menus[menuId];
  const menuLabel = menu?.label || "Menu";
  const mobileMenus = getMobileMenus(custom_forms_menus);
  const mobileCustomForms = getMobileCustomForms(custom_forms);

  const childMenus = _(mobileMenus)
    .filter((m) => m.parent_type === "custom_menu" && m.parent_id === menu?.id)
    .sortBy("order")
    .value();

  const menuForms = _.filter(mobileCustomForms, (form) => _.some(menu?.custom_forms, (cf) => cf.id === form.id));

  let items = menu
    ? _.concat(createMobileCustomFormLinks(menuForms), createMobileMenuItems(childMenus, custom_forms))
    : [];

  if (menu) {
    if (menu.parent_type === "custom_menu") {
      const parentMenu = custom_forms_menus[menu.parent_id];
      const parentName = parentMenu?.menu_type === "module" ? parentMenu.label : menu.parent_label;
      items.push({
        label: `Back To ${parentName || "Menu"}`,
        link: `/custom_forms/menus/${menu.parent_id}/module`,
      });
    } else {
      items.push(parentBackLinks[menu.parent_type] || { label: "Back To Lion", link: "/" });
    }
  }

  return { menuLabel, items };
};

export const SignOutListItem = () => (
  // ponytail: F7 only treats .item-link as a tap target on Android; pad so the last row
  // isn't sitting in the system nav/gesture inset (clicks never fire there)
  <List style={{ paddingBottom: "max(48px, var(--f7-safe-area-bottom, 0px))" }}>
    <ListItem
      title="Sign out"
      link
      href={false}
      onClick={async () => {
        await logout();
        window.location.assign("/");
      }}
    />
    <ListItem
      title="Clear local data"
      link
      href={false}
      onClick={async () => {
        if (!window.confirm("Remove cached records and unsynced work on this device?")) return;
        await clearAllLocalData();
        window.location.assign("/");
      }}
    />
  </List>
);

export const MobileNavList = ({ items }) => {
  if (_.isEmpty(items)) return null;

  const regularItems = _.filter(items, (item) => !item.content);
  const accordionItems = _.filter(items, (item) => item.content);

  return (
    <Fragment>
      {!_.isEmpty(regularItems) && (
        <List>
          {_.map(regularItems, (item, i) => (
            <ListItem
              key={i}
              link={item.link || item.to}
              external={item.external}
              title={item.label}
              panelClose
            />
          ))}
        </List>
      )}
      {_.map(accordionItems, (item, i) => (
        <List key={`accordion-${i}`} accordionList>
          <ListItem accordionItem title={item.label}>
            <AccordionContent>
              <List>
                {_.map(item.content, (subItem, j) => (
                  <ListItem key={j} link={subItem.to} title={subItem.label} panelClose />
                ))}
              </List>
            </AccordionContent>
          </ListItem>
        </List>
      ))}
    </Fragment>
  );
};
